import React from "react";
import { motion } from "framer-motion";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Megaphone, Download, Copy, Instagram, Facebook } from "lucide-react";

const B = "https://static.prod-images.emergentagent.com/jobs/f66e0e14-b56e-4de5-b0fd-8ad35555866a/images/";

const CREATIVES = [
  {
    id: "portail-square", format: "Feed carré (1080×1080)", ratio: "square",
    img: B + "096458833f503a834c82021d54348a933d37a2ca4d0309706aed23a24fbf320c.png",
    title: "Un seul portail",
    caption: "🏗️ Toute votre entreprise de construction, pilotée depuis UN seul portail.\nPointage, CRM, projets, soumissions IA et comptabilité — enfin réunis.\n👉 progere.ca\n\n#construction #Québec #PROGÈRE #gestion #CCQ #entrepreneur",
  },
  {
    id: "conformite-square", format: "Feed carré (1080×1080)", ratio: "square",
    img: B + "a357139b9663f3c56b2a3f3b04de8e11bf346967abf9ba78d8941f6fe92e5cec.png",
    title: "Conforme Québec",
    caption: "✅ Conforme au Québec : TPS/TVQ, CCQ et Loi 25 gérés automatiquement.\nConcentrez-vous sur vos chantiers — on s'occupe de la conformité.\n👉 progere.ca\n\n#CCQ #Québec #construction #conformité #Loi25 #TPSTVQ",
  },
  {
    id: "punch-square", format: "Feed carré (1080×1080)", ratio: "square",
    img: B + "ab9930b377b6cdbeb26eead30d9f8ac789a9879c70fe136704bdc04e3574a238.png",
    title: "PROGÈRE PUNCH",
    caption: "⏱️ Pointage, présence et paie CCQ enfin simplifiés avec PROGÈRE PUNCH.\nGéolocalisation chantier + feuilles de temps automatiques.\n👉 progere.ca\n\n#construction #paie #CCQ #pointage #PROGÈREPUNCH",
  },
  {
    id: "devis-square", format: "Feed carré (1080×1080)", ratio: "square",
    img: B + "65ea7c56c33e2ddeefe96c7c469429b9a0c9f3d27c35c861b06dfc690504aed2.png",
    title: "PROGÈRE DEVIS AI",
    caption: "🤖 Des soumissions professionnelles en quelques minutes grâce à l'IA.\nDécrivez le projet, PROGÈRE DEVIS AI fait le reste.\n👉 progere.ca\n\n#estimation #soumission #IA #construction #Québec",
  },
  {
    id: "portail-story", format: "Story verticale (1080×1920)", ratio: "story",
    img: B + "595a4f55d6d5e55ebe0df5fe1ffad1217898c322448452d708653ec910e12a55.png",
    title: "Story — Un seul portail",
    caption: "🏗️ Toute votre entreprise de construction. Un seul portail.\nDécouvrez PROGÈRE 👉 progere.ca\n\n#construction #Québec #PROGÈRE",
  },
  {
    id: "punch-story", format: "Story verticale (1080×1920)", ratio: "story",
    img: B + "c76ca92eb8eb7ee38d6da8da89e69ee7e0d0ce5710497201aadc33c3215c4fbb.png",
    title: "Story — Temps réel",
    caption: "⏱️ Pointage & présence en temps réel. Feuilles de temps et paie CCQ automatisées.\nEssayez PROGÈRE PUNCH 👉 progere.ca\n\n#construction #paie #CCQ",
  },
  {
    id: "crm-square", format: "Feed carré (1080×1080)", ratio: "square",
    img: B + "dfea62a4f19308df577e607f127c7dae98a8c48c53b9b3cf9ec47e9059109eee.png",
    title: "PROGÈRE CRM",
    caption: "🤝 Ne perdez plus jamais un client. Suivez chaque prospect et relance au bon moment avec PROGÈRE CRM.\n👉 progere.ca\n\n#construction #ventes #CRM #Québec #entrepreneur",
  },
  {
    id: "compta-square", format: "Feed carré (1080×1080)", ratio: "square",
    img: B + "fd70a5c11c804bdb063c0498acbd6fafab9581cd9f88eb5346e9694a1b5f02c8.png",
    title: "PROGÈRE COMPTA",
    caption: "🧾 Facturation et TPS/TVQ automatiques. Fini les erreurs de taxes — vos chiffres sont toujours à jour.\n👉 progere.ca\n\n#comptabilité #TPSTVQ #construction #Québec #PROGÈRE",
  },
  {
    id: "erp-square", format: "Feed carré (1080×1080)", ratio: "square",
    img: B + "1d9b572e5ff8cdf497c96675720b775e82c154e9a2ccb1744a453ebed2874d51.png",
    title: "PROGÈRE ERP",
    caption: "📊 Vos projets et échéanciers, sous contrôle. Planifiez, suivez les budgets et les ressources en un coup d'œil.\n👉 progere.ca\n\n#gestiondeprojet #construction #ERP #chantier #Québec",
  },
  {
    id: "offer-square", format: "Feed carré (1080×1080)", ratio: "square",
    img: B + "479a47d5bfa1dbc210fc3f78f8653ee6083264dc037c6c306569a9ff385ff4ca.png",
    title: "L'écosystème du Québec",
    caption: "🍁 L'écosystème pensé pour les entrepreneurs en construction du Québec. Commencez aujourd'hui.\n👉 progere.ca\n\n#construction #Québec #entrepreneur #PROGÈRE #CCQ",
  },
  {
    id: "productivity-story", format: "Story verticale (1080×1920)", ratio: "story",
    img: B + "b112d9f250220957cc95a770a95bed35f342819a9cb5585ceddec20df67c8d68.png",
    title: "Story — 10 h par semaine",
    caption: "⏳ Gagnez jusqu'à 10 h par semaine. Automatisez la paperasse et la paie avec PROGÈRE.\n👉 progere.ca\n\n#productivité #construction #Québec",
  },
  {
    id: "teams-story", format: "Story verticale (1080×1920)", ratio: "story",
    img: B + "fe40da43e9eb29f7ff31824e40c45d0396f2a64d2565c387787a7818cf5fa7b4.png",
    title: "Story — Temps réel",
    caption: "📱 Vos équipes, vos chantiers, vos chiffres — en temps réel. Essayez PROGÈRE.\n👉 progere.ca\n\n#construction #tempsréel #Québec",
  },
];

async function downloadImage(url, filename) {
  try {
    const res = await fetch(url, { mode: "cors" });
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(objectUrl);
    toast.success("Image téléchargée");
  } catch {
    window.open(url, "_blank");
    toast.info("Faites un clic droit → Enregistrer l'image");
  }
}

export default function MediaKitPage() {
  const { user } = useAuth();
  if (user && !user.is_staff) return <Navigate to="/app" replace />;

  const copyCaption = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Légende copiée !"); }
    catch { toast.error("Impossible de copier"); }
  };

  return (
    <motion.div className="space-y-8" data-testid="media-kit-page" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      <div>
        <h1 className="text-3xl font-extrabold text-white flex items-center gap-2.5"><Megaphone className="w-6 h-6 text-[#EAB308]" /> Kit média</h1>
        <p className="mt-1 text-white/55">Visuels publicitaires prêts à publier pour vos réseaux — téléchargez l'image et copiez la légende.</p>
        <div className="mt-3 flex items-center gap-3 text-xs text-white/45">
          <span className="inline-flex items-center gap-1"><Instagram className="w-4 h-4" /> Instagram</span>
          <span className="inline-flex items-center gap-1"><Facebook className="w-4 h-4" /> Facebook</span>
        </div>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
        {CREATIVES.map((c) => (
          <div key={c.id} className="flex flex-col rounded-xl border border-white/10 bg-[#0a0a0a] overflow-hidden" data-testid={`creative-${c.id}`}>
            <div className={`relative bg-black ${c.ratio === "story" ? "aspect-[9/16]" : "aspect-square"}`}>
              <img src={c.img} alt={c.title} className="h-full w-full object-cover" loading="lazy" />
              <span className="absolute top-3 left-3 rounded-full bg-black/70 border border-white/15 px-2.5 py-1 text-[10px] font-semibold text-white/80">{c.format}</span>
            </div>
            <div className="flex flex-1 flex-col p-4">
              <h3 className="font-bold text-white">{c.title}</h3>
              <p className="mt-2 flex-1 whitespace-pre-line text-xs text-white/55 leading-relaxed">{c.caption}</p>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <button onClick={() => downloadImage(c.img, `progere-${c.id}.png`)} data-testid={`download-${c.id}`} className="flex items-center justify-center gap-1.5 rounded-md bg-[#EAB308] text-black py-2 text-xs font-semibold hover:opacity-90 transition-opacity">
                  <Download className="w-4 h-4" /> Télécharger
                </button>
                <button onClick={() => copyCaption(c.caption)} data-testid={`copy-${c.id}`} className="flex items-center justify-center gap-1.5 rounded-md border border-white/12 bg-white/5 py-2 text-xs font-semibold text-white/80 hover:text-white transition-colors">
                  <Copy className="w-4 h-4" /> Copier la légende
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-[#EAB308]/20 bg-[#EAB308]/[0.05] p-6 text-sm text-white/70">
        <p className="font-semibold text-white">💡 Conseils de publication</p>
        <ul className="mt-2 list-disc pl-5 space-y-1">
          <li>Publiez les <b>carrés</b> dans le fil Instagram/Facebook, les <b>stories verticales</b> en Story/Reel.</li>
          <li>Ajoutez votre lien <b>progere.ca</b> en bio et un bouton « En savoir plus » sur vos pubs payantes.</li>
          <li>Meilleurs moments au Québec : tôt le matin (6h-8h) et fin de journée (16h-18h), en semaine.</li>
        </ul>
      </div>
    </motion.div>
  );
}
